import React, { useState } from 'react';
import { AlertCircle, CheckCircle, FileText, Shield, TrendingUp } from 'lucide-react';
import SEO from './SEO';
// Removed unused imports

const RiskAssessmentTool = () => {
  const [step, setStep] = useState(1);
  const [showReport, setShowReport] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [formData, setFormData] = useState({
    industry: '',
    employeeCount: '',
    revenue: '',
    operations: [],
    numberOfFacilities: '',
    facilities: [],
    hazards: [],
    products: [],
    psmChemicals: [],
    states: [],
    insuranceType: '',
    hasPollutionCoverage: false
  });

  // ADD PURCHASE
  const handlePurchase = async () => {
  // TEMPORARY DEBUG - Remove after fixing
  console.log('🔑 Stripe Key being used:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
    try {
      // Show loading state
      const buttons = document.querySelectorAll('button');
      const purchaseButton = Array.from(buttons).find(btn =>
        btn.textContent.includes('Purchase Comprehensive Report')
      );

      if (purchaseButton) {
        purchaseButton.textContent = 'Processing...';
        purchaseButton.disabled = true;
      }

      // Data should already be in sessionStorage
      const storedFormData = sessionStorage.getItem('assessmentFormData');
      const storedReport = sessionStorage.getItem('comprehensiveReport');

      if (!storedFormData || !storedReport) {
        console.warn('⚠️ Data not found in session, storing now...');
        const comprehensiveReport = generateComprehensiveReport();
        sessionStorage.setItem('assessmentFormData', JSON.stringify(formData));
        sessionStorage.setItem('comprehensiveReport', JSON.stringify(comprehensiveReport));
      }

      const assessmentData = {
        company: formData.industry,
        employees: formData.employeeCount,
        revenue: formData.revenue,
        facilities: formData.facilities.length,
        states: formData.states,
        timestamp: new Date().toISOString()
      };

      // Call your serverless function
      const response = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ assessmentData }),
      });

      if (!response.ok) {
        throw new Error('Payment processing failed');
      }

      const { url } = await response.json();

      // 🔍 TEMPORARY DEBUG - Check the session ID
      console.log('🔗 Checkout URL:', url);
      if (url.includes('cs_test_')) {
        console.error('❌ PROBLEM: Backend is creating TEST session!');
        console.error('This means STRIPE_SECRET_KEY in Vercel is still sk_test_');
      } else if (url.includes('cs_live_')) {
        console.log('✅ GOOD: Backend is creating LIVE session!');
      }

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      alert('Payment processing error. Please try again or contact info@firststepehs.com');
      console.error('Payment error:', error);

      // Reset button
      const buttons = document.querySelectorAll('button');
      const purchaseButton = Array.from(buttons).find(btn =>
        btn.textContent.includes('Processing')
      );
      if (purchaseButton) {
        purchaseButton.textContent = 'Purchase Comprehensive Report ($299)';
        purchaseButton.disabled = false;
      }
    }
  };

  const industries = [
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'industrial', label: 'Industrial' },
    { value: 'petrochemical', label: 'Petrochemical' },
    { value: 'warehousing', label: 'Warehousing & Distribution' },
    { value: 'construction', label: 'Construction' },
    { value: 'food-processing', label: 'Food Processing' },
    { value: 'chemical', label: 'Chemical Manufacturing' },
    { value: 'healthcare', label: 'Healthcare Facilities' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'laboratory', label: 'Laboratory' },
    { value: 'servicing', label: 'Servicing and/or Installation' }
  ];

  const operationTypes = [
    'Manufacturing', 'Warehousing', 'Field Operations', 'Laboratory Work',
    'Fleet Operations', 'Installation, Service, Maintenance, or Repair', 'Office Only'
  ];

  const hazardTypes = [
    'Hazardous Chemicals', 'Hazardous Waste Generation', 'Fuel Storage',
    'Heavy Machinery', 'Confined Spaces', 'Hot Work', 
    'Ammonia Refrigeration', 'Combustible Dust', 'Municipal, Construction, and Debris Disposal',
    'Battery Storage'
  ];

  // PSM Threshold Chemicals - shown as a separate category
  const psmChemicals = [
    'Ammonia (>10,000 lbs)',
    'Chlorine (>1,500 lbs)',
    'Hydrogen (>10,000 lbs)',
    'Propane (>10,000 lbs)',
    'Ethylene Oxide (>5,000 lbs)',
    'Hydrogen Sulfide (>1,500 lbs)',
    'Phosgene (>100 lbs)',
    'Sulfur Dioxide (>1,000 lbs)',
    'Vinyl Chloride (>1,000 lbs)',
    'Flammable Liquids/Gases (>10,000 lbs)',
    'Other PSM-Covered Chemical'
  ];

  const productTypes = [
    'PFAS', 'PFOA', 'Glyphosate', 'Other Emerging Contaminants'
  ];

  const states = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
    'Connecticut', 'Delaware', 'District of Columbia', 'Florida', 'Georgia',
    'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
    'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota',
    'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
    'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia',
    'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ];
  const stateEnvironmentalAgencies = {
    'Alabama': { name: 'Dept. of Environmental Management (ADEM)', url: 'https://adem.alabama.gov/' },
    'Alaska': { name: 'Dept. of Environmental Conservation (DEC)', url: 'http://dec.alaska.gov/' },
    'Arizona': { name: 'Dept. of Environmental Quality (ADEQ)', url: 'http://www.azdeq.gov/' },
    'Arkansas': { name: 'Dept. of Environmental Quality (ADEQ)', url: 'http://www.adeq.state.ar.us/' },
    'California': { name: 'Environmental Protection Agency (Cal/EPA)', url: 'http://www.calepa.ca.gov/' },
    'Colorado': { name: 'Dept. of Public Health & Environment (DPHE)', url: 'http://www.cdphe.state.co.us/' },
    'Connecticut': { name: 'Dept. of Energy & Environmental Protection (DEEP)', url: 'http://www.ct.gov/deep/' },
    'Delaware': { name: 'Dept. of Natural Resources & Environmental Control (DNREC)', url: 'http://www.dnrec.delaware.gov/' },
    'District of Columbia': { name: 'District Dept. of Environment (DDOE)', url: 'http://ddoe.dc.gov/service/environmental-protection' },
    'Florida': { name: 'Dept. of Environmental Protection (DEP)', url: 'http://www.dep.state.fl.us/' },
    'Georgia': { name: 'Environmental Protection Division (EPD)', url: 'http://www.gaepd.org/' },
    'Hawaii': { name: 'Dept. of Land & Natural Resources (DLNR)', url: 'http://dlnr.hawaii.gov/' },
    'Idaho': { name: 'Dept. of Environmental Quality (DEQ)', url: 'http://www.deq.idaho.gov/' },
    'Illinois': { name: 'Environmental Protection Agency (ILEPA)', url: 'http://www.epa.illinois.gov/' },
    'Indiana': { name: 'Dept. of Environmental Management (IDEM)', url: 'http://www.state.in.us/idem/' },
    'Iowa': { name: 'Dept. of Natural Resources (DNR)', url: 'http://www.iowadnr.gov/' },
    'Kansas': { name: 'Dept. of Health & Environment (KDHE)', url: 'http://www.kdheks.gov/' },
    'Kentucky': { name: 'Dept. for Environmental Protection (DEP)', url: 'http://dep.ky.gov/' },
    'Louisiana': { name: 'Dept. of Environmental Quality (DEQ)', url: 'http://www.deq.louisiana.gov/' },
    'Maine': { name: 'Dept. of Environmental Protection (DEP)', url: 'http://www.maine.gov/dep/index.shtml' },
    'Maryland': { name: 'Dept. of the Environment (MDE)', url: 'http://www.mde.state.md.us/' },
    'Massachusetts': { name: 'Dept. of Environmental Protection (DEP)', url: 'http://www.mass.gov/eea/agencies/massdep/' },
    'Michigan': { name: 'Dept. of Environment, Great Lakes, and Energy (EGLE)', url: 'http://www.michigan.gov/egle' },
    'Minnesota': { name: 'Pollution Control Agency (PCA)', url: 'http://www.pca.state.mn.us/' },
    'Mississippi': { name: 'Dept. of Environmental Quality (MDEQ)', url: 'https://www.mdeq.ms.gov/' },
    'Missouri': { name: 'Dept. of Natural Resources (DNR)', url: 'http://www.dnr.mo.gov/' },
    'Montana': { name: 'Dept. of Environmental Quality (DEQ)', url: 'http://www.deq.mt.gov/' },
    'Nebraska': { name: 'Dept. of Environmental Quality (DEQ)', url: 'http://www.deq.state.ne.us/' },
    'Nevada': { name: 'Division of Environmental Protection (DEP)', url: 'http://ndep.nv.gov/index.htm' },
    'New Hampshire': { name: 'Dept. of Environmental Services (DES)', url: 'http://www.des.state.nh.us/' },
    'New Jersey': { name: 'Dept. of Environmental Protection (DEP)', url: 'http://www.state.nj.us/dep/' },
    'New Mexico': { name: 'Environment Dept. (NMED)', url: 'https://www.env.nm.gov/' },
    'New York': { name: 'Dept. of Environmental Conservation (DEC)', url: 'http://www.dec.ny.gov/' },
    'North Carolina': { name: 'Dept. of Environmental Quality (DEQ)', url: 'http://deq.nc.gov/' },
    'North Dakota': { name: 'Dept. of Health (Environmental Health Section)', url: 'http://www.ndhealth.gov/EHS/' },
    'Ohio': { name: 'Environmental Protection Agency (EPA)', url: 'http://www.epa.state.oh.us/' },
    'Oklahoma': { name: 'Dept. of Environmental Quality (DEQ)', url: 'http://www.deq.state.ok.us/' },
    'Oregon': { name: 'Dept. of Environmental Quality (DEQ)', url: 'http://www.oregon.gov/DEQ/' },
    'Pennsylvania': { name: 'Dept. of Environmental Protection (DEP)', url: 'http://www.dep.pa.gov/' },
    'Rhode Island': { name: 'Dept. of Environmental Management (DEM)', url: 'http://www.dem.ri.gov/' },
    'South Carolina': { name: 'Dept. of Health & Environmental Control (DHEC)', url: 'http://www.scdhec.net/' },
    'South Dakota': { name: 'Dept. of Environment & Natural Resources (DENR)', url: 'http://denr.sd.gov/' },
    'Tennessee': { name: 'Dept. of Environment & Conservation (TDEC)', url: 'http://www.tn.gov/environment/' },
    'Texas': { name: 'Commission on Environmental Quality (TCEQ)', url: 'https://www.tceq.texas.gov/' },
    'Utah': { name: 'Dept. of Environmental Quality (DEQ)', url: 'http://www.deq.utah.gov/' },
    'Vermont': { name: 'Dept. of Environmental Conservation (DEC)', url: 'http://dec.vermont.gov/' },
    'Virginia': { name: 'Dept. of Environmental Quality (DEQ)', url: 'http://www.deq.state.va.us/' },
    'Washington': { name: 'Dept. of Ecology', url: 'http://www.ecy.wa.gov/' },
    'West Virginia': { name: 'Dept of Environmental Protection (DEP)', url: 'http://www.dep.wv.gov/' },
    'Wisconsin': { name: 'Dept of Natural Resources (DNR)', url: 'http://dnr.wi.gov/' },
    'Wyoming': { name: 'Dept. of Environmental Quality (DEQ)', url: 'http://deq.wyoming.gov/' }
  };

  const oshaStatePlans = {
    'Alaska': true, 'Arizona': true, 'California': true, 'Hawaii': true,
    'Indiana': true, 'Iowa': true, 'Kentucky': true, 'Maryland': true,
    'Michigan': true, 'Minnesota': true, 'Nevada': true, 'New Mexico': true,
    'North Carolina': true, 'Oregon': true, 'South Carolina': true,
    'Tennessee': true, 'Utah': true, 'Vermont': true, 'Virginia': true,
    'Washington': true, 'Wyoming': true
  };
  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      if (field === 'numberOfFacilities') {
        const count = parseInt(value) || 0;
        updated.facilities = Array(count).fill(null).map((_, i) => ({
          id: i + 1,
          type: '',
          age: '',
          squareFootage: '',
          location: '',
          airEmissions: false,
          waterDischarges: false,
          hazardousWaste: false,
          hazardousMaterialsStorage: false,
          permits: [],
          dischargeType: '',
          pastRemediation: false,
          reportableSpills: false,
          brownfields: false,
          insuranceRequirements: false
        }));
      }
      
      return updated;
    });
  };

  const handleFacilityChange = (facilityId, field, value) => {
    setFormData(prev => ({
      ...prev,
      facilities: prev.facilities.map(f => 
        f.id === facilityId ? { ...f, [field]: value } : f
      )
    }));
  };

  const handleFacilityMultiSelect = (facilityId, field, value) => {
    setFormData(prev => ({
      ...prev,
      facilities: prev.facilities.map(f => 
        f.id === facilityId 
          ? {
              ...f,
              [field]: f[field].includes(value)
                ? f[field].filter(item => item !== value)
                : [...f[field], value]
            }
          : f
      )
    }));
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const generateReport = () => {
    setShowReport(true);
  };
  const generateExecutiveSummary = () => {
    const summary = {
      companyProfile: {
        industry: formData.industry,
        employees: formData.employeeCount,
        revenue: formData.revenue,
        facilities: formData.facilities.length,
        states: formData.states
      },
      topRisks: [],
      priorityActions: []
    };

    const risks = [];
    
    if (formData.facilities.some(f => f.age === '50+')) {
      risks.push({ 
        risk: 'Asbestos/Lead-Based Paint', 
        severity: 'High',
        priority: 1,
        action: 'Commission AHERA-compliant asbestos survey and lead-based paint inspection',
        timeline: '30 days',
        estimatedCost: '$5,000-$15,000 per facility'
      });
    }

    if (formData.facilities.some(f => f.airEmissions)) {
      risks.push({
        risk: 'Air Quality Permits',
        severity: 'High',
        priority: 1,
        action: 'Review Title V applicability and state air permit requirements',
        timeline: '30-60 days',
        estimatedCost: '$3,000-$25,000+ depending on permit type'
      });
    }

    if (formData.facilities.some(f => f.waterDischarges)) {
      risks.push({
        risk: 'Water Discharge Compliance',
        severity: 'High',
        priority: 1,
        action: 'Verify NPDES permit status and DMR compliance',
        timeline: '30 days',
        estimatedCost: '$2,000-$10,000 for permit application/renewal'
      });
    }

    if (!formData.hasPollutionCoverage && (
      formData.facilities.some(f => f.hazardousWaste || f.hazardousMaterialsStorage) ||
      formData.hazards.includes('Hazardous Chemicals')
    )) {
      risks.push({
        risk: 'Missing Pollution Liability Insurance',
        severity: 'High',
        priority: 2,
        action: 'Obtain pollution liability coverage quotes',
        timeline: '30-45 days',
        estimatedCost: '$5,000-$50,000+ annual premium'
      });
    }

    summary.topRisks = risks.slice(0, 5);
    summary.priorityActions = risks.map(r => ({
      action: r.action,
      timeline: r.timeline,
      cost: r.estimatedCost
    }));

    return summary;
  };
  const generateFacilityAnalysis = () => {
    return formData.facilities.map(facility => {
      const analysis = {
        facilityId: facility.id,
        regulatoryProfile: [],
        commonInspectionFindings: []
      };

      // Age-based requirements
      if (facility.age === '50+') {
        analysis.regulatoryProfile.push({
          regulation: 'Asbestos NESHAP (40 CFR Part 61, Subpart M)',
          trigger: 'Buildings constructed before 1980',
          requirement: 'Asbestos survey required before renovation/demolition activities',
          actionItems: [
            'Commission AHERA-compliant asbestos survey by accredited inspector',
            'Develop O&M program if ACM is present',
            'Submit NESHAP notification 10 working days before renovation/demolition',
            'Use licensed asbestos contractor for abatement'
          ],
          links: [
            { text: 'EPA Asbestos NESHAP', url: 'https://www.epa.gov/asbestos/asbestos-neshap' }
          ],
          costs: {
            survey: '$2,500-$5,000 per building',
            abatement: '$15-$30 per square foot',
            omProgram: '$1,500-$3,000 annually'
          }
        });

        analysis.regulatoryProfile.push({
          regulation: 'Lead-Based Paint (40 CFR Part 745)',
          trigger: 'Buildings constructed before 1978',
          requirement: 'Lead-based paint inspection and risk assessment',
          actionItems: [
            'Commission lead-based paint inspection by certified inspector',
            'Implement lead-safe work practices for renovation',
            'Provide lead hazard information to employees',
            'EPA RRP certification required for contractors'
          ],
          costs: {
            inspection: '$300-$700 per unit/area',
            rrpCertification: '$300 per firm'
          }
        });
      }

      // Air emissions requirements
      if (facility.airEmissions) {
        analysis.regulatoryProfile.push({
          regulation: 'Clean Air Act Title V Operating Permits (40 CFR Part 70)',
          trigger: 'Major sources of air pollution (≥100 tons/year)',
          requirement: 'Operating permit required for major sources',
          actionItems: [
            'Calculate potential to emit (PTE) for all air emission sources',
            'Determine Title V applicability',
            'Apply for state air permit if threshold exceeded',
            'Implement continuous emission monitoring if required',
            'Submit annual emission inventory reports'
          ],
          costs: {
            engineeringStudy: '$5,000-$15,000',
            titleVPermit: '$10,000-$50,000+ application fee',
            statePermit: '$3,000-$25,000',
            monitoring: '$2,000-$10,000+ annually'
          }
        });

        analysis.commonInspectionFindings.push(
          'Incomplete emission calculations',
          'Missing stack testing records',
          'Failure to submit emission inventory reports',
          'Inadequate recordkeeping for fuel usage'
        );
      }
      // Water discharge requirements
      if (facility.waterDischarges) {
        analysis.regulatoryProfile.push({
          regulation: 'NPDES Permits (Clean Water Act, 40 CFR Parts 122-125)',
          trigger: 'Any discharge of pollutants to surface waters',
          requirement: 'NPDES permit required for all point source discharges',
          actionItems: [
            'Apply for NPDES permit or verify existing permit is current',
            'Implement discharge monitoring program',
            'Submit Discharge Monitoring Reports (DMRs) monthly/quarterly',
            'Develop SWPPP for stormwater discharges',
            'Conduct required sampling and analysis'
          ],
          costs: {
            permitApplication: '$2,000-$10,000',
            laboratoryCosts: '$1,500-$5,000+ per quarter',
            swpppDevelopment: '$2,500-$7,500',
            annualFees: '$500-$5,000+'
          }
        });

        analysis.commonInspectionFindings.push(
          'Late or incomplete DMR submissions',
          'Permit limit exceedances without proper reporting',
          'Inadequate stormwater pollution prevention plan',
          'Missing sampling/analysis procedures'
        );
      }

      // Hazardous waste requirements
      if (facility.hazardousWaste) {
        analysis.regulatoryProfile.push({
          regulation: 'RCRA Hazardous Waste Management (40 CFR Parts 260-279)',
          trigger: 'Generation of hazardous waste',
          requirement: 'Generator status determination and compliance',
          actionItems: [
            'Conduct waste characterization for all waste streams',
            'Determine generator status (VSQG, SQG, or LQG)',
            'Obtain EPA ID number',
            'Implement compliant waste storage practices',
            'Comply with accumulation time limits',
            'Complete hazardous waste manifests'
          ],
          generatorCategories: {
            vsqg: 'Very Small: <100 kg/month',
            sqg: 'Small: 100-1,000 kg/month',
            lqg: 'Large: >1,000 kg/month'
          },
          costs: {
            wasteCharacterization: '$500-$2,000 per waste stream',
            disposal: '$0.50-$15+ per pound',
            training: '$500-$2,000 annually'
          }
        });

        analysis.commonInspectionFindings.push(
          'Improper waste characterization',
          'Unlabeled containers',
          'Exceeding accumulation time limits',
          'Inadequate contingency plan'
        );
      }

      // Location-specific requirements
      if (facility.location === 'waterway') {
        analysis.regulatoryProfile.push({
          regulation: 'SPCC Plan Requirements (40 CFR Part 112)',
          trigger: 'Oil storage >1,320 gallons aggregate',
          requirement: 'SPCC plan required with PE certification',
          actionItems: [
            'Calculate total oil storage capacity',
            'Prepare SPCC plan with PE certification',
            'Implement secondary containment',
            'Conduct annual inspections',
            'Train employees on spill response'
          ],
          costs: {
            planPreparation: '$3,000-$8,000',
            secondaryContainment: '$5,000-$50,000+',
            annualInspection: '$500-$2,000'
          }
        });
      }

      return analysis;
    });
  };
  const generateRegulatoryCompliance = () => {
    const compliance = {
      oshaRequirements: [],
      stateRequirements: []
    };

    if (formData.hazards.includes('Hazardous Chemicals')) {
      compliance.oshaRequirements.push({
        standard: 'Hazard Communication Standard (29 CFR 1910.1200)',
        requirement: 'Written HazCom program, SDS availability, container labeling',
        actionItems: [
          'Develop written Hazard Communication program',
          'Maintain SDS for all hazardous chemicals',
          'Label all chemical containers',
          'Provide initial and annual HazCom training'
        ],
        training: 'Initial training upon hire and when new hazards introduced',
        costs: {
          programDevelopment: '$1,500-$3,000',
          training: '$50-$150 per employee',
          sdsManagement: '$500-$1,500 annually'
        }
      });
    }

    if (formData.hazards.includes('Confined Spaces')) {
      compliance.oshaRequirements.push({
        standard: 'Permit-Required Confined Spaces (29 CFR 1910.146)',
        requirement: 'Written program, atmospheric testing, entry permits',
        actionItems: [
          'Identify and evaluate all confined spaces',
          'Develop written confined space program',
          'Train authorized entrants, attendants, and supervisors',
          'Implement atmospheric testing protocols',
          'Establish rescue procedures'
        ],
        costs: {
          programDevelopment: '$3,000-$7,000',
          equipment: '$2,000-$8,000',
          training: '$200-$500 per employee'
        }
      });
    }

    if (formData.hazards.includes('Heavy Machinery')) {
      compliance.oshaRequirements.push({
        standard: 'Lockout/Tagout (29 CFR 1910.147)',
        requirement: 'LOTO program, authorized employee training, annual inspections',
        actionItems: [
          'Develop written LOTO program',
          'Identify all energy sources for equipment',
          'Develop machine-specific LOTO procedures',
          'Train authorized and affected employees',
          'Conduct annual LOTO inspections'
        ],
        costs: {
          programDevelopment: '$2,500-$6,000',
          locksAndTags: '$500-$2,000',
          training: '$150-$300 per employee'
        }
      });
    }

    if (formData.psmChemicals.length > 0) {
      compliance.oshaRequirements.push({
        standard: 'Process Safety Management (29 CFR 1910.119)',
        requirement: 'Comprehensive PSM program for processes involving highly hazardous chemicals',
        actionItems: [
          'Conduct Process Hazard Analysis (PHA) using recognized methodology (HAZOP, What-If, etc.)',
          'Develop and maintain process safety information (PSI) for all covered processes',
          'Implement written operating procedures for all process phases',
          'Establish mechanical integrity program for critical equipment',
          'Develop management of change (MOC) procedures',
          'Conduct pre-startup safety reviews (PSSR) for new or modified processes',
          'Provide specialized employee and contractor training',
          'Investigate incidents and near-misses within 48 hours',
          'Conduct compliance audits every 3 years',
          'Develop emergency action and response plans',
          'Implement hot work permit program',
          'Maintain and update Process Hazard Analysis every 5 years'
        ],
        training: 'Initial training, refresher training every 3 years, and when processes change',
        citations: 'PSM is consistently among top 10 most cited OSHA standards with penalties often exceeding $100,000 per violation',
        costs: {
          initialPHA: '$25,000-$100,000 per process',
          psmProgramDevelopment: '$50,000-$150,000',
          mechanicalIntegrity: '$10,000-$50,000 annually',
          training: '$500-$1,500 per employee',
          complianceAudit: '$15,000-$40,000 every 3 years',
          consultant: '$150-$300 per hour for PSM specialist'
        },
        chemicalsPresent: formData.psmChemicals.join(', ')
      });
    }

    // Add state-specific requirements
    formData.states.forEach(state => {
      const hasStatePlan = oshaStatePlans[state];
      
      compliance.stateRequirements.push({
        state: state,
        environmentalAgency: stateEnvironmentalAgencies[state],
        oshaProgram: hasStatePlan ? 'State-Run OSHA Program' : 'Federal OSHA',
        oshaNote: hasStatePlan 
          ? 'State operates OSHA-approved program with potentially different standards'
          : 'Federal OSHA standards apply'
      });
    });

    return compliance;
  };
  const generateImplementationRoadmap = () => {
    const roadmap = {
      immediate: [],
      shortTerm: [],
      mediumTerm: [],
      longTerm: []
    };

    roadmap.immediate.push(
      { action: 'Conduct facility walkthrough to identify hazards', cost: 'Internal time', priority: 'Critical' },
      { action: 'Review current insurance policies', cost: '$0', priority: 'Critical' },
      { action: 'Compile chemical inventory and SDS library', cost: 'Internal time', priority: 'High' }
    );

    if (formData.facilities.some(f => f.age === '50+')) {
      roadmap.immediate.push({
        action: 'Commission asbestos and lead surveys',
        cost: '$5,000-$15,000',
        priority: 'Critical'
      });
    }

    if (formData.psmChemicals.length > 0) {
      roadmap.immediate.push({
        action: 'Engage PSM consultant to assess current compliance status',
        cost: '$5,000-$15,000',
        priority: 'Critical'
      });
    }

    roadmap.shortTerm.push(
      { action: 'Develop written EHS programs', cost: '$5,000-$15,000', priority: 'High' },
      { action: 'Conduct employee safety training', cost: '$100-$300 per employee', priority: 'High' }
    );

    if (!formData.hasPollutionCoverage) {
      roadmap.shortTerm.push({
        action: 'Obtain pollution liability insurance',
        cost: '$5,000-$50,000+ annual premium',
        priority: 'High'
      });
    }

    if (formData.psmChemicals.length > 0) {
      roadmap.shortTerm.push({
        action: 'Initiate Process Hazard Analysis (PHA) for all covered processes',
        cost: '$25,000-$100,000 per process',
        priority: 'High'
      });
      roadmap.shortTerm.push({
        action: 'Develop Process Safety Information (PSI) and operating procedures',
        cost: '$20,000-$60,000',
        priority: 'High'
      });
    }

    roadmap.shortTerm.push(
      { action: 'Develop written EHS programs', cost: '$5,000-$15,000', priority: 'High' },
      { action: 'Conduct employee safety training', cost: '$100-$300 per employee', priority: 'High' }
    );

    if (!formData.hasPollutionCoverage) {
      roadmap.shortTerm.push({
        action: 'Obtain pollution liability insurance',
        cost: '$5,000-$50,000+ annual premium',
        priority: 'High'
      });
    }

    roadmap.mediumTerm.push(
      { action: 'Conduct comprehensive compliance audit', cost: '$10,000-$30,000', priority: 'Medium-High' },
      { action: 'Implement corrective actions', cost: 'Varies', priority: 'Medium-High' }
    );

    roadmap.longTerm.push(
      { action: 'Establish ongoing compliance monitoring', cost: '$5,000-$20,000 annually', priority: 'Medium' },
      { action: 'Consider internal EHS staff', cost: '$75,000-$150,000 annually', priority: 'Medium' }
    );

    return roadmap;
  };

  const generateCitationIndex = () => {
    return [
      {
        category: 'Federal Environmental Regulations',
        citations: [
          { title: 'Clean Air Act - Title V', cite: '40 CFR Part 70', link: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-C/part-70' },
          { title: 'NPDES Permits', cite: '40 CFR Parts 122-125', link: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-D' },
          { title: 'RCRA Hazardous Waste', cite: '40 CFR Parts 260-279', link: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I' },
          { title: 'SPCC Requirements', cite: '40 CFR Part 112', link: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-D/part-112' },
          { title: 'Asbestos NESHAP', cite: '40 CFR Part 61 Subpart M', link: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-C/part-61/subpart-M' }
        ]
      },
      {
        category: 'OSHA Safety Standards',
        citations: [
          { title: 'Hazard Communication', cite: '29 CFR 1910.1200', link: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.1200' },
          { title: 'Lockout/Tagout', cite: '29 CFR 1910.147', link: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.147' },
          { title: 'Confined Spaces', cite: '29 CFR 1910.146', link: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.146' }
        ]
      }
    ];
  };

  const generateComprehensiveReport = () => {
    return {
      executiveSummary: generateExecutiveSummary(),
      facilityAnalysis: generateFacilityAnalysis(),
      regulatoryCompliance: generateRegulatoryCompliance(),
      implementationRoadmap: generateImplementationRoadmap(),
      citations: generateCitationIndex()
    };
  };
  const getEHSRisks = () => {
    const risks = [];
    
    const hasAirEmissions = formData.facilities.some(f => f.airEmissions);
    const hasWaterDischarges = formData.facilities.some(f => f.waterDischarges);
    const hasHazardousWaste = formData.facilities.some(f => f.hazardousWaste);
    const hasHazmatStorage = formData.facilities.some(f => f.hazardousMaterialsStorage);
    const hasPastRemediation = formData.facilities.some(f => f.pastRemediation);
    const hasReportableSpills = formData.facilities.some(f => f.reportableSpills);
    const inBrownfields = formData.facilities.some(f => f.brownfields);
    const nearWaterway = formData.facilities.some(f => f.location === 'waterway');
    const inResidential = formData.facilities.some(f => f.location === 'residential');
    const hasOldFacility = formData.facilities.some(f => f.age === '50+');
    
    if (hasOldFacility) {
      risks.push({
        category: 'Asbestos & Lead-Based Paint',
        description: 'Facilities built before 1980 commonly contain asbestos-containing materials (ACM) and lead-based paint. Pre-renovation surveys, O&M programs, and worker protection requirements under NESHAP and OSHA regulations are frequently overlooked.',
        likelihood: 'High'
      });
    }

    if (inResidential) {
      risks.push({
        category: 'Community Relations & Nuisance Claims',
        description: 'Operations in residential areas face heightened scrutiny regarding potential releases, emissions, odors, noise, and other nuisances. These facilities are more likely to receive community complaints.',
        likelihood: 'Medium-High'
      });
    }

    if (nearWaterway) {
      risks.push({
        category: 'Waterway Discharge & Runoff Controls',
        description: 'Facilities near waterways require SPCC plans, stormwater management programs, and enhanced containment systems. Many facilities lack adequate secondary containment or current SPCC certifications.',
        likelihood: 'High'
      });
    }

    if (hasHazmatStorage) {
      risks.push({
        category: 'TSCA Chemical Reporting & Inventory',
        description: 'Facilities storing hazardous materials may be subject to TSCA reporting requirements and EPCRA Tier II emergency planning notifications. Missing chemical inventories are common compliance gaps.',
        likelihood: 'Medium-High'
      });
    }

    if (formData.hazards.includes('Battery Storage')) {
      risks.push({
        category: 'Battery Storage & TSCA Compliance',
        description: 'Large-scale battery storage systems may trigger TSCA reporting requirements and specific fire safety standards. Many facilities underestimate regulatory requirements for battery installations.',
        likelihood: 'Medium-High'
      });
    }

    if (formData.products.length > 0) {
      risks.push({
        category: 'Emerging Contaminants & Product Liability',
        description: `Products containing ${formData.products.join(', ')} face increasing regulatory scrutiny. Multiple states have enacted restrictions and manufacturers may face disclosure requirements and toxic tort litigation.`,
        likelihood: 'High'
      });
    }
    
    if (formData.hazards.includes('Hazardous Chemicals')) {
      risks.push({
        category: 'Hazard Communication (HazCom)',
        description: 'Facilities with hazardous chemical storage are commonly cited for incomplete Safety Data Sheets (SDS), inadequate labeling, and lack of employee training documentation.',
        likelihood: 'High'
      });
    }

    if (hasAirEmissions) {
      risks.push({
        category: 'Air Quality Permits & Compliance',
        description: 'Facilities with air emissions face regular scrutiny for Title V permits, emission monitoring, and reporting requirements. Missing or incomplete stack testing records are common citation areas.',
        likelihood: 'High'
      });
    }

    if (hasWaterDischarges) {
      risks.push({
        category: 'NPDES/Water Discharge Compliance',
        description: 'Water discharge operations require NPDES permits and regular monitoring. Violations commonly occur with discharge monitoring reports (DMRs) and exceedance reporting.',
        likelihood: 'High'
      });
    }

    if (hasHazardousWaste) {
      risks.push({
        category: 'Hazardous Waste Management (RCRA)',
        description: 'Hazardous waste facilities must comply with RCRA requirements including proper characterization, storage time limits, manifesting, and training. Common gaps include inadequate contingency plans.',
        likelihood: 'Medium-High'
      });
    }

    if (hasPastRemediation || hasReportableSpills) {
      risks.push({
        category: 'Historical Environmental Liability',
        description: 'Sites with past remediation or reportable spills carry ongoing monitoring and reporting obligations. These create potential insurance exclusions and require careful disclosure.',
        likelihood: 'Medium-High'
      });
    }

    if (inBrownfields) {
      risks.push({
        category: 'Brownfields Program Obligations',
        description: 'Participation in brownfields programs requires ongoing institutional controls, land use restrictions, and periodic reporting. These obligations transfer with property.',
        likelihood: 'Medium'
      });
    }

    if (formData.hazards.includes('Confined Spaces')) {
      risks.push({
        category: 'Permit-Required Confined Space',
        description: 'Organizations with confined spaces typically lack complete entry permits, atmospheric testing records, and rescue plan documentation.',
        likelihood: 'Medium-High'
      });
    }

    if (formData.states.includes('California') && formData.hazards.includes('Hazardous Chemicals')) {
      risks.push({
        category: 'Cal/OSHA & CUPA Requirements',
        description: 'California operations face heightened scrutiny for Injury & Illness Prevention Programs (IIPP), hazardous materials business plans, and Proposition 65 compliance.',
        likelihood: 'High'
      });
    }

    if (formData.psmChemicals.length > 0) {
      risks.push({
        category: 'Process Safety Management (PSM) Compliance',
        description: `Facilities storing or processing ${formData.psmChemicals.join(', ')} at threshold quantities must comply with OSHA's PSM standard (29 CFR 1910.119). This includes process hazard analysis (PHA), mechanical integrity programs, management of change procedures, pre-startup safety reviews, emergency planning, compliance audits, and incident investigation. PSM violations carry significant penalties and are among OSHA's most serious citations.`,
        likelihood: 'High'
      });
    }

    if (risks.length === 0) {
      risks.push({
        category: 'General Industry Standards',
        description: 'Most facilities face exposure related to general safety training, emergency action plans, and recordkeeping requirements.',
        likelihood: 'Medium'
      });
    }

    return risks;
  };
  const getInsuranceConsiderations = () => {
    const considerations = [];

    const hasInsuranceRequirements = formData.facilities.some(f => f.insuranceRequirements);
    const hasPastRemediation = formData.facilities.some(f => f.pastRemediation);
    const hasReportableSpills = formData.facilities.some(f => f.reportableSpills);
    const hasAirEmissions = formData.facilities.some(f => f.airEmissions);
    const hasWaterDischarges = formData.facilities.some(f => f.waterDischarges);
    const hasHazardousWaste = formData.facilities.some(f => f.hazardousWaste);
    const hasHazmatStorage = formData.facilities.some(f => f.hazardousMaterialsStorage);
    
    const hasProducts = formData.products.length > 0;
    const isContractor = formData.operations.includes('Installation, Service, Maintenance, or Repair') || 
                         formData.industry === 'construction';
    const isConsulting = formData.industry === 'consulting';
    
    const hasEnvironmentalRisk = hasAirEmissions || hasWaterDischarges || hasHazardousWaste || 
                                  hasHazmatStorage || formData.hazards.includes('Hazardous Chemicals') ||
                                  formData.hazards.includes('Hazardous Waste Generation');

    // Universal insurance considerations
    const universalConsiderations = {
      area: 'Essential Policy Provisions',
      insight: 'All insurance programs should include: (1) Blanket Additional Insured with Primary & Non-Contributory and Waiver of Subrogation. (2) Per Project Aggregate limits. (3) Notice of Cancellation provisions (30-60 days). (4) Defense costs outside limits.',
      coverage: 'Policy Enhancements'
    };

    // Separate shopping recommendation
    const shoppingRecommendation = {
      area: 'Annual Market Review',
      insight: 'Insurance markets are dynamic and competitive. Organizations should conduct annual market reviews to ensure optimal pricing and coverage terms. Carrier appetite, underwriting guidelines, and pricing can vary significantly year-to-year. Working with a broker who has access to multiple carriers is essential for securing the best terms.',
      coverage: 'Best Practice'
    };

    // If "Unsure" is selected
    if (formData.insuranceType === 'unsure') {
      considerations.push({
        area: 'Baseline Insurance Program',
        insight: 'At minimum, organizations should carry Commercial General Liability (CGL) coverage for third-party bodily injury and property damage protection.',
        coverage: 'CGL (Minimum)'
      });

      if (hasEnvironmentalRisk) {
        considerations.push({
          area: 'Environmental Insurance Coverage Options',
          insight: 'Environmental risks require dedicated pollution liability coverage. Options include: Contractors Pollution Liability (CPL), Transportation Pollution, Non-Owned Disposal Site Liability (NODSL), Products Pollution, and Premises Pollution/Site Environmental Liability (sudden & accidental or time element coverage).',
          coverage: 'Pollution Liability (Multiple Forms)'
        });

        if (hasProducts) {
          considerations.push({
            area: 'Products Pollution & Site Coverage',
            insight: 'Organizations with products should carry both Products Pollution Liability and Site/Premises Pollution coverage to address product-related and operational environmental risks.',
            coverage: 'Products Pollution + Site Pollution'
          });
        }

        if (isContractor) {
          considerations.push({
            area: 'Contractors Pollution Liability',
            insight: 'Contracting operations at third-party locations require Contractors Pollution Liability (CPL). This coverage addresses pollution conditions from your work at project sites and is often contractually required.',
            coverage: 'Contractors Pollution Liability (CPL)'
          });
        }
      }

      if (isConsulting) {
        considerations.push({
          area: 'Professional Liability (E&O)',
          insight: 'Consulting, testing, and monitoring services require Professional Liability coverage for claims from professional negligence, errors in judgment, or failure to perform services properly.',
          coverage: 'Professional Liability / E&O'
        });
      }

      considerations.push({
        area: 'Excess Liability & Umbrella Coverage',
        insight: 'Consider excess or umbrella liability to provide additional limits above primary policies. Umbrella policies may broaden coverage by filling gaps in underlying policies.',
        coverage: 'Excess / Umbrella'
      });

      considerations.push({
        area: 'Captive Insurance Consideration',
        insight: 'Organizations with $500K+ annual premium spend or difficult-to-place risks may benefit from captive structures. Captives allow risk retention, cost stabilization, and reinsurance access. This is not a requirement but worth evaluating as operations scale.',
        coverage: 'Optional Structure'
      });

      considerations.push(universalConsiderations);
            considerations.push(shoppingRecommendation);
    }
    // If Standard Market, Surplus Lines, Layered Program, or Captive is selected
    if (['standard', 'surplus', 'layered', 'captive'].includes(formData.insuranceType)) {
      
      if (!formData.hasPollutionCoverage && hasEnvironmentalRisk) {
        considerations.push({
          area: 'Missing Pollution Liability Coverage',
          insight: 'Your operations present environmental exposure, but pollution coverage is not in place. Evaluate: Contractors Pollution Liability (CPL), Transportation Pollution, Non-Owned Disposal Site Liability (NODSL), Products Pollution, and Premises Pollution/Site Environmental Liability.',
          coverage: 'Pollution Liability (Multiple Forms)'
        });

        if (hasProducts) {
          considerations.push({
            area: 'Products Pollution & Site Coverage Needed',
            insight: 'Given your product operations, consider both Products Pollution Liability and Site/Premises Pollution coverage to address product-related and operational environmental risks.',
            coverage: 'Products Pollution + Site Pollution'
          });
        }

        if (isContractor) {
          considerations.push({
            area: 'Contractors Pollution Liability Needed',
            insight: 'Your contracting operations at third-party locations create pollution exposure excluded under standard CGL. Contractors Pollution Liability (CPL) is essential and often contractually required.',
            coverage: 'Contractors Pollution Liability (CPL)'
          });
        }
      }

      if (isConsulting && !formData.hasPollutionCoverage) {
        considerations.push({
          area: 'Professional Liability Required',
          insight: 'Consulting, testing, and monitoring services require Professional Liability (E&O) coverage for claims from professional negligence. This is distinct from pollution liability.',
          coverage: 'Professional Liability / E&O'
        });
      }

      if (formData.insuranceType === 'standard') {
        considerations.push({
          area: 'Standard Market Placement Review',
          insight: 'Your standard market placement provides traditional coverage forms. As operations grow, you may need surplus lines for specialized coverages or higher limits.',
          coverage: 'Standard Market'
        });
      }

      if (formData.insuranceType === 'surplus') {
        considerations.push({
          area: 'Surplus Lines Market Placement',
          insight: 'Surplus lines provide flexibility for difficult-to-place risks. However, surplus carriers are non-admitted and lack state guaranty fund protection. Ensure your broker has surplus lines expertise.',
          coverage: 'Surplus Lines'
        });
      }

      if (formData.insuranceType === 'layered') {
        considerations.push({
          area: 'Layered Program Structure Review',
          insight: 'Your layered program provides extended liability limits. Ensure excess layers follow form with primary coverage and aggregates are properly structured. Review attachment points annually.',
          coverage: 'Layered Program'
        });
      }

      if (formData.insuranceType === 'captive') {
        considerations.push({
          area: 'Captive Insurance Program Optimization',
          insight: 'Your captive provides control over risk retention. Ensure adequate capitalization, regulatory compliance, and that retained risks are appropriate for your financial position.',
          coverage: 'Captive Program'
        });
      }

      considerations.push({
        area: 'Excess/Umbrella Liability Evaluation',
        insight: 'Review whether current excess or umbrella limits are adequate. Consider contractual requirements and whether additional layers provide cost-effective protection.',
        coverage: 'Excess / Umbrella Review'
      });

      considerations.push({
        area: 'Captive Insurance Consideration',
        insight: 'Organizations with $500K+ annual premium, consistent loss history, or difficult-to-place risks may benefit from captive structures. Captives require regulatory compliance but can provide long-term cost savings. This is not a requirement but worth evaluating.',
        coverage: 'Optional Structure'
      });

      considerations.push(universalConsiderations);
            considerations.push(shoppingRecommendation);
    }

    if (hasInsuranceRequirements) {
      considerations.push({
        area: 'Lease/Mortgage Insurance Requirements',
        insight: 'Landlords and lenders commonly require specific coverage types, limits, and additional insured endorsements. Non-compliance can trigger default provisions.',
        coverage: 'Per Lease/Mortgage Terms'
      });
    }

    if (hasPastRemediation || hasReportableSpills) {
      considerations.push({
        area: 'Environmental Impairment Liability',
        insight: 'Sites with known contamination require specialized environmental insurance. Standard pollution exclusions apply. Full disclosure during underwriting is critical.',
        coverage: 'EIL / Site Pollution'
      });
    }

    if (formData.hazards.includes('Ammonia Refrigeration')) {
      considerations.push({
        area: 'Specialized Equipment Coverage',
        insight: 'Ammonia systems trigger PSM/RMP requirements and specialized insurance needs. Many standard carriers exclude refrigeration system failures.',
        coverage: 'Equipment Breakdown + Environmental'
      });
    }

    if (parseInt(formData.employeeCount) > 250 || formData.revenue === '50m+') {
      considerations.push({
        area: 'Enterprise Risk Management',
        insight: 'Organizations of this size typically require EPL, D&O, Fiduciary Liability, and Cyber coverage. These are commonly required by lenders, investors, and boards.',
        coverage: 'EPL + D&O + Cyber + Fiduciary'
      });
    }

    return considerations;
  };

  const getRecommendations = () => {
    const recs = [];

    if (formData.hazards.length > 2) {
      recs.push('Consider engaging a qualified EHS consultant to conduct a comprehensive compliance gap analysis.');
    }

    if (formData.states.includes('California') || formData.hazards.includes('Hazardous Chemicals')) {
      recs.push('Organizations with this profile typically benefit from a pre-renewal environmental compliance audit.');
    }

    if (formData.industry === 'manufacturing' || formData.operations.includes('Manufacturing')) {
      recs.push('Manufacturing operations frequently require updated safety programs, employee training records, and equipment inspection logs.');
    }

    recs.push('A professional insurance broker with expertise in this industry segment can identify coverage gaps and optimize premium spend.');

    return recs;
  };
  if (showReport) {
    const ehsRisks = getEHSRisks();
    const insuranceConsiderations = getInsuranceConsiderations();
    const recommendations = getRecommendations();

    if (showUpgrade) {
      const comprehensiveReport = generateComprehensiveReport();
      
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-lg shadow-xl p-8">
              <h1 className="text-3xl font-bold text-slate-800 mb-6">Upgrade Your Assessment</h1>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="border-2 border-blue-600 rounded-lg p-6 bg-blue-50">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-slate-800">Comprehensive Report</h3>
                    <span className="text-2xl font-bold text-blue-600">$299</span>
                  </div>
                  <p className="text-slate-700 mb-4">Get a detailed, professional-grade Stage 1 EHS & Insurance Risk Assessment with:</p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} />
                      <span className="text-slate-700"><strong>Executive Summary</strong> with risk severity matrix and priority timeline</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} />
                      <span className="text-slate-700"><strong>Facility-by-Facility Analysis</strong> with CFR citations</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} />
                      <span className="text-slate-700"><strong>Regulatory Compliance Deep Dive</strong> with permit requirements</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} />
                      <span className="text-slate-700"><strong>Cost Estimates</strong> for compliance actions and permits</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} />
                      <span className="text-slate-700"><strong>Insurance Gap Analysis</strong> with policy recommendations</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} />
                      <span className="text-slate-700"><strong>Implementation Roadmap</strong> with 30/60/90/180-day plan</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} />
                      <span className="text-slate-700"><strong>Citation Index</strong> with links to regulations and forms</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} />
                      <span className="text-slate-700"><strong>Professional PDF</strong> for stakeholders and lenders</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} />
                      <span className="text-slate-700"><strong>AI-Ready Data File</strong> - Create your AI EHS Personal Assistant - Upload to ChatGPT/Claude/Gemini/Co-Pilot for unlimited ongoing compliance questions</span>
                    </li>
                  </ul>
                  <button
                    onClick={handlePurchase}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Purchase Comprehensive Report ($299)
                  </button>
                  <div className="mt-4 p-3 bg-white rounded border border-blue-200">
                    <p className="text-xs text-slate-600">
                      <strong>Your Report Includes:</strong> {comprehensiveReport.facilityAnalysis.length} facility analyses, {comprehensiveReport.regulatoryCompliance.oshaRequirements.length} OSHA requirements, guidance for {formData.states.length} state(s).
                    </p>
                  </div>
                </div>

                <div className="border-2 border-green-600 rounded-lg p-6 bg-green-50">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-slate-800">Professional Consultation</h3>
                    <span className="text-sm text-slate-600 bg-white px-3 py-1 rounded-full">Custom Quote</span>
                  </div>
                  <p className="text-slate-700 mb-4">Connect with vetted professionals who specialize in your risk profile:</p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} />
                      <span className="text-slate-700"><strong>Environmental Attorneys</strong> - Regulatory compliance, permits, enforcement defense</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} />
                      <span className="text-slate-700"><strong>EHS Consultants</strong> - Gap assessments, program development, training</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} />
                      <span className="text-slate-700"><strong>Insurance Brokers</strong> - Specialized environmental coverage placement</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={20} />
                      <span className="text-slate-700"><strong>Remediation Specialists</strong> - Site assessment, cleanup, brownfields</span>
                    </li>
                  </ul>
                  <button 
                    onClick={() => alert('Professional Network Coming Soon!\n\nWe are building a vetted network of environmental attorneys, EHS consultants, insurance brokers, and remediation specialists. Check back soon!')}
  className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                  >
                  Browse Professional Network
                  </button>
                </div>
              </div>
              {/* Report Content Preview */}
              <div className="border-t pt-8 mb-8">
                <h2 className="text-2xl font-semibold text-slate-800 mb-4">Comprehensive Report Preview</h2>
                <p className="text-slate-600 mb-6">Here's a sample of what your detailed report includes:</p>

                {/* Executive Summary Preview - KEEP BUT LIMIT */}
                <div className="mb-6 p-6 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center">
                    <FileText className="mr-2 text-blue-600" size={20} />
                    Executive Summary (Sample)
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-slate-700">Company Profile:</p>
                      <p className="text-sm text-slate-600">
                        {comprehensiveReport.executiveSummary.companyProfile.industry} industry, {comprehensiveReport.executiveSummary.companyProfile.employees} employees, {comprehensiveReport.executiveSummary.companyProfile.facilities} facilities
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">Sample Priority Actions (showing 2 of {comprehensiveReport.executiveSummary.priorityActions.length}):</p>
                      <ul className="text-sm text-slate-600 space-y-1 ml-4">
                        {comprehensiveReport.executiveSummary.priorityActions.slice(0, 2).map((action, idx) => (
                          <li key={idx} className="list-disc">
                            {action.action} ({action.timeline})
                          </li>
                        ))}
                      </ul>
                      {comprehensiveReport.executiveSummary.priorityActions.length > 2 && (
                        <p className="text-xs text-blue-600 mt-2 italic">
                          + {comprehensiveReport.executiveSummary.priorityActions.length - 2} more actions in full report
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Facility Analysis Preview - SHOW ONLY FIRST REGULATION */}
                {comprehensiveReport.facilityAnalysis.length > 0 && comprehensiveReport.facilityAnalysis[0].regulatoryProfile.length > 0 && (
                  <div className="mb-6 p-6 bg-slate-50 rounded-lg border border-slate-200 relative">
                    <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center">
                      <Shield className="mr-2 text-blue-600" size={20} />
                      Facility Analysis Sample (1 of {comprehensiveReport.facilityAnalysis.length} facilities)
                    </h3>

                    {/* Show only FIRST regulation */}
                    <div className="mb-4 pb-4">
                      <p className="font-semibold text-slate-800 text-sm mb-1">
                        {comprehensiveReport.facilityAnalysis[0].regulatoryProfile[0].regulation}
                      </p>
                      <p className="text-xs text-slate-600 mb-2">
                        <strong>Trigger:</strong> {comprehensiveReport.facilityAnalysis[0].regulatoryProfile[0].trigger}
                      </p>
                      <p className="text-xs text-slate-600 mb-2"><strong>Sample Actions (showing 2):</strong></p>
                      <ul className="text-xs text-slate-600 space-y-1 ml-4">
                        {comprehensiveReport.facilityAnalysis[0].regulatoryProfile[0].actionItems.slice(0, 2).map((action, i) => (
                          <li key={i} className="list-disc">{action}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Blur overlay for additional content */}
                    <div className="relative">
                      <div className="blur-sm opacity-50">
                        <p className="text-xs text-slate-600 mb-1"><strong>Additional Requirements:</strong></p>
                        <ul className="text-xs text-slate-600 space-y-1 ml-4 mb-3">
                          <li className="list-disc">Complete regulatory analysis with CFR citations</li>
                          <li className="list-disc">Detailed cost breakdowns for each requirement</li>
                          <li className="list-disc">Direct links to regulations and guidance</li>
                        </ul>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold shadow-lg">
                          🔒 Full Details in Paid Report
                        </div>
                      </div>
                    </div>

                    <p className="text-xs text-blue-600 mt-4">
                      Full report includes {comprehensiveReport.facilityAnalysis[0].regulatoryProfile.length} regulatory requirements for this facility plus {comprehensiveReport.facilityAnalysis.length - 1} additional {comprehensiveReport.facilityAnalysis.length - 1 === 1 ? 'facility' : 'facilities'}
                    </p>
                  </div>
                )}

                {/* OSHA Requirements - TEASER ONLY */}
                {comprehensiveReport.regulatoryCompliance.oshaRequirements.length > 0 && (
                  <div className="mb-6 p-6 bg-slate-50 rounded-lg border border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center">
                      <AlertCircle className="mr-2 text-orange-600" size={20} />
                      OSHA Requirements (Sample)
                    </h3>
                    <p className="text-sm text-slate-700 mb-2">
                      Your comprehensive report includes detailed analysis of <strong>{comprehensiveReport.regulatoryCompliance.oshaRequirements.length} OSHA standards</strong> applicable to your operations:
                    </p>
                    <ul className="text-sm text-slate-600 space-y-1 ml-4">
                      {comprehensiveReport.regulatoryCompliance.oshaRequirements.map((req, i) => (
                        <li key={i} className="list-disc">{req.standard}</li>
                      ))}
                    </ul>
                    <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
                      <p className="text-xs text-slate-700">
                        Each standard includes: specific action items, training requirements, cost estimates, and links to OSHA resources
                      </p>
                    </div>
                  </div>
                )}

                {/* Implementation Roadmap - HIGH LEVEL ONLY */}
                <div className="mb-6 p-6 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center">
                    <TrendingUp className="mr-2 text-purple-600" size={20} />
                    Implementation Roadmap
                  </h3>
                  <p className="text-sm text-slate-700 mb-3">Your phased action plan includes:</p>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="bg-white p-3 rounded border border-slate-200">
                      <p className="font-semibold text-sm text-slate-800">Immediate (0-30 days)</p>
                      <p className="text-xs text-slate-600">{comprehensiveReport.implementationRoadmap.immediate.length} critical actions</p>
                    </div>
                    <div className="bg-white p-3 rounded border border-slate-200">
                      <p className="font-semibold text-sm text-slate-800">Short-Term (30-90 days)</p>
                      <p className="text-xs text-slate-600">{comprehensiveReport.implementationRoadmap.shortTerm.length} high-priority actions</p>
                    </div>
                    <div className="bg-white p-3 rounded border border-slate-200">
                      <p className="font-semibold text-sm text-slate-800">Medium-Term (90-180 days)</p>
                      <p className="text-xs text-slate-600">{comprehensiveReport.implementationRoadmap.mediumTerm.length} actions</p>
                    </div>
                    <div className="bg-white p-3 rounded border border-slate-200">
                      <p className="font-semibold text-sm text-slate-800">Long-Term (180+ days)</p>
                      <p className="text-xs text-slate-600">{comprehensiveReport.implementationRoadmap.longTerm.length} strategic actions</p>
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 mt-3">
                    Each action includes specific timelines, cost estimates, and priority ratings
                  </p>
                </div>

                {/* Citation Index - SUMMARY ONLY */}
                <div className="p-6 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center">
                    <FileText className="mr-2 text-green-600" size={20} />
                    Citation Index & Resources
                  </h3>
                  <p className="text-sm text-slate-700 mb-3">Complete regulatory reference library with:</p>
                  <div className="grid md:grid-cols-3 gap-3">
                    <div className="bg-white p-3 rounded border border-slate-200">
                      <p className="text-xs font-semibold text-slate-800 mb-1">Federal Regulations</p>
                      <p className="text-xs text-slate-600">{comprehensiveReport.citations[0].citations.length} citations with direct CFR links</p>
                    </div>
                    <div className="bg-white p-3 rounded border border-slate-200">
                      <p className="text-xs font-semibold text-slate-800 mb-1">OSHA Standards</p>
                      <p className="text-xs text-slate-600">{comprehensiveReport.citations[1].citations.length} standards with regulatory links</p>
                    </div>
                    <div className="bg-white p-3 rounded border border-slate-200">
                      <p className="text-xs font-semibold text-slate-800 mb-1">State Resources</p>
                      <p className="text-xs text-slate-600">{formData.states.length} state agencies with direct contact info</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6 flex gap-4 justify-center">
                <button
                  onClick={() => setShowUpgrade(false)}
                  className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Back to Basic Report
                </button>
                <button
                  onClick={() => {
                    setShowUpgrade(false);
                    setShowReport(false);
                    setStep(1);
                  }}
                  className="px-6 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                >
                  Start New Assessment
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-lg shadow-xl p-8 mb-6">
            <div className="border-b pb-4 mb-6">
              <h1 className="text-3xl font-bold text-slate-800">EHS & Insurance Readiness Report</h1>
              <p className="text-slate-600 mt-2">Preliminary risk and compliance insight based on operational profile</p>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-8">
              <div className="flex items-start">
                <AlertCircle className="text-amber-600 mr-3 flex-shrink-0 mt-1" size={20} />
                <div className="text-sm text-amber-800">
                  <strong>Important Disclaimer:</strong> This report provides educational information about common compliance patterns. It does not constitute legal advice, insurance advice, or a guarantee of coverage or compliance.
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4 flex items-center">
                <Shield className="mr-3 text-blue-600" size={24} />
                EHS Risk Signals
              </h2>
              
              <div className="space-y-4">
                {ehsRisks.map((risk, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-slate-800 text-lg">{risk.category}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        risk.likelihood === 'High' ? 'bg-red-100 text-red-700' :
                        risk.likelihood === 'Medium-High' ? 'bg-orange-100 text-orange-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {risk.likelihood}
                      </span>
                    </div>
                    <p className="text-slate-700">{risk.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4 flex items-center">
                <FileText className="mr-3 text-green-600" size={24} />
                Insurance Coverage Considerations
              </h2>
              
              <div className="space-y-4">
                {insuranceConsiderations.map((item, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-slate-800 text-lg">{item.area}</h3>
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {item.coverage}
                      </span>
                    </div>
                    <p className="text-slate-700">{item.insight}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4 flex items-center">
                <TrendingUp className="mr-3 text-purple-600" size={24} />
                Next-Step Recommendations
              </h2>
              
              <div className="space-y-3">
                {recommendations.map((rec, idx) => (
                  <div key={idx} className="flex items-start bg-slate-50 rounded-lg p-4">
                    <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} />
                    <p className="text-slate-700">{rec}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-6 text-center">
              <div className="flex gap-4 justify-center mb-4">
                <button
                  onClick={() => {
                    // Generate and store data BEFORE showing upgrade page
                    const comprehensiveReport = generateComprehensiveReport();
                    sessionStorage.setItem('assessmentFormData', JSON.stringify(formData));
                    sessionStorage.setItem('comprehensiveReport', JSON.stringify(comprehensiveReport));
                    console.log('✅ Assessment data stored for purchase');
                    setShowUpgrade(true);
                  }}
                  className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  Get Comprehensive Report ($299)
                </button>
                <button
                  onClick={() => setShowUpgrade(true)}
                  className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                >
                  Contact a Professional
                </button>
              </div>
              <button
                onClick={() => {
                  setShowReport(false);
                  setStep(1);
                }}
                className="mt-4 px-6 py-2 text-slate-600 hover:text-slate-800 transition-colors"
              >
                Start New Assessment
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <SEO
        title="Free EHS Risk Assessment Tool - First Step EHS"
        description="Take our free 10-minute EHS assessment to identify workplace safety gaps, OSHA compliance issues, and insurance readiness. Get instant results and optional comprehensive report."
        keywords="EHS assessment tool, workplace safety assessment, OSHA compliance check, risk assessment, safety audit tool"
        canonical="https://firststepehs.com/assessment"
      />

    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">EHS & Insurance Readiness Snapshot</h1>
            <p className="text-slate-600">Get preliminary risk and compliance insights in minutes</p>
          </div>

          <div className="flex items-center justify-between mb-8">
            {[1, 2, 3, 4, 5].map(s => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= s ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {s}
                </div>
                {s < 5 && <div className={`w-16 h-1 ${step > s ? 'bg-blue-600' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-800">Company Profile</h2>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Industry</label>
                <select
                  value={formData.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select industry...</option>
                  {industries.map(ind => (
                    <option key={ind.value} value={ind.value}>{ind.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Number of Employees</label>
                <select
                  value={formData.employeeCount}
                  onChange={(e) => handleInputChange('employeeCount', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select range...</option>
                  <option value="1-50">1-50</option>
                  <option value="51-250">51-250</option>
                  <option value="251-1000">251-1,000</option>
                  <option value="1000+">1,000+</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Annual Revenue</label>
                <select
                  value={formData.revenue}
                  onChange={(e) => handleInputChange('revenue', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select range...</option>
                  <option value="<5m">Less than $5M</option>
                  <option value="5-25m">$5M - $25M</option>
                  <option value="25-50m">$25M - $50M</option>
                  <option value="50m+">$50M+</option>
                </select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-800">Operations</h2>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">Operation Types (Select all that apply)</label>
                <div className="grid grid-cols-2 gap-3">
                  {operationTypes.map(op => (
                    <label key={op} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.operations.includes(op)}
                        onChange={() => handleMultiSelect('operations', op)}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-slate-700">{op}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Number of Facilities</label>
                <select
                  value={formData.numberOfFacilities}
                  onChange={(e) => handleInputChange('numberOfFacilities', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5+</option>
                </select>
              </div>
            </div>
          )}
          {step === 3 && formData.facilities.length > 0 && (
            <div className="space-y-8">
              <h2 className="text-xl font-semibold text-slate-800">Facility Details</h2>
              <p className="text-sm text-slate-600">Provide information for each facility</p>
              
              {formData.facilities.map((facility, idx) => (
                <div key={facility.id} className="border border-slate-200 rounded-lg p-6 bg-slate-50">
                  <h3 className="font-semibold text-slate-800 mb-4">Facility {idx + 1}</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Facility Type</label>
                      <select
                        value={facility.type}
                        onChange={(e) => handleFacilityChange(facility.id, 'type', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select...</option>
                        <option value="owned">Owned</option>
                        <option value="leased">Leased</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Facility Age</label>
                      <select
                        value={facility.age}
                        onChange={(e) => handleFacilityChange(facility.id, 'age', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select...</option>
                        <option value="<10">Less than 10 years</option>
                        <option value="10-25">10-25 years</option>
                        <option value="25-50">25-50 years</option>
                        <option value="50+">50+ years</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Square Footage</label>
                      <select
                        value={facility.squareFootage}
                        onChange={(e) => handleFacilityChange(facility.id, 'squareFootage', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select...</option>
                        <option value="<25k">Less than 25,000 sq ft</option>
                        <option value="25-100k">25,000 - 100,000 sq ft</option>
                        <option value="100-250k">100,000 - 250,000 sq ft</option>
                        <option value="250k+">250,000+ sq ft</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Location Type</label>
                      <select
                        value={facility.location}
                        onChange={(e) => handleFacilityChange(facility.id, 'location', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select...</option>
                        <option value="industrial">Industrial Area</option>
                        <option value="commercial">Commercial Area</option>
                        <option value="residential">Residential Area</option>
                        <option value="waterway">Near Waterway</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    <p className="text-sm font-medium text-slate-700">Environmental Aspects</p>
                    <div className="grid md:grid-cols-3 gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={facility.airEmissions}
                          onChange={(e) => handleFacilityChange(facility.id, 'airEmissions', e.target.checked)}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-slate-700 text-sm">Air Emissions</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={facility.waterDischarges}
                          onChange={(e) => handleFacilityChange(facility.id, 'waterDischarges', e.target.checked)}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-slate-700 text-sm">Water Discharges</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={facility.hazardousWaste}
                          onChange={(e) => handleFacilityChange(facility.id, 'hazardousWaste', e.target.checked)}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-slate-700 text-sm">Hazardous Waste Generation</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={facility.hazardousMaterialsStorage}
                          onChange={(e) => handleFacilityChange(facility.id, 'hazardousMaterialsStorage', e.target.checked)}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-slate-700 text-sm">Hazardous Materials Storage</span>
                      </label>
                    </div>
                  </div>

                  {facility.waterDischarges && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-slate-700 mb-2">Discharge Type</label>
                      <select
                        value={facility.dischargeType}
                        onChange={(e) => handleFacilityChange(facility.id, 'dischargeType', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select...</option>
                        <option value="sewer">Municipal Sewer</option>
                        <option value="treatment">Water Treatment Facility</option>
                        <option value="direct">Direct Discharge (NPDES)</option>
                      </select>
                    </div>
                  )}

                  {(facility.airEmissions || facility.waterDischarges || facility.hazardousWaste) && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-slate-700 mb-2">Permit Types (Select all that apply)</label>
                      <div className="grid md:grid-cols-2 gap-2">
                        {['Air Quality', 'NPDES', 'RCRA', 'Stormwater', 'Title V', 'Other'].map(permit => (
                          <label key={permit} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={facility.permits.includes(permit)}
                              onChange={() => handleFacilityMultiSelect(facility.id, 'permits', permit)}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="text-slate-700 text-sm">{permit}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 space-y-3">
                    <p className="text-sm font-medium text-slate-700">Historical Issues</p>
                    <div className="grid md:grid-cols-2 gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={facility.pastRemediation}
                          onChange={(e) => handleFacilityChange(facility.id, 'pastRemediation', e.target.checked)}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-slate-700 text-sm">Past Remediation</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={facility.reportableSpills}
                          onChange={(e) => handleFacilityChange(facility.id, 'reportableSpills', e.target.checked)}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-slate-700 text-sm">Past Reportable Spills</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={facility.brownfields}
                          onChange={(e) => handleFacilityChange(facility.id, 'brownfields', e.target.checked)}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-slate-700 text-sm">Brownfields Program</span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={facility.insuranceRequirements}
                        onChange={(e) => handleFacilityChange(facility.id, 'insuranceRequirements', e.target.checked)}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-slate-700 text-sm font-medium">Lease or mortgage has specific insurance requirements</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
          {step === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-800">Hazards & Products</h2>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">Hazard Types Present (Select all that apply)</label>
                <div className="grid grid-cols-2 gap-3">
                  {hazardTypes.map(hazard => (
                    <label key={hazard} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.hazards.includes(hazard)}
                        onChange={() => handleMultiSelect('hazards', hazard)}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-slate-700 text-sm">{hazard}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                <label className="block text-sm font-medium text-red-900 mb-2">
                  ⚠️ Process Safety Management (PSM) Chemicals
                </label>
                <p className="text-xs text-red-800 mb-3">
                  Select if you store or process any of these chemicals at or above threshold quantities (29 CFR 1910.119)
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {psmChemicals.map(chemical => (
                    <label key={chemical} className="flex items-center space-x-2 cursor-pointer bg-white p-2 rounded">
                      <input
                        type="checkbox"
                        checked={formData.psmChemicals.includes(chemical)}
                        onChange={() => handleMultiSelect('psmChemicals', chemical)}
                        className="w-5 h-5 text-red-600 rounded focus:ring-2 focus:ring-red-500"
                      />
                      <span className="text-slate-700 text-sm">{chemical}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">Products Made or Sold (Select all that apply)</label>
                <p className="text-xs text-slate-500 mb-3">Products containing emerging contaminants of concern</p>
                <div className="grid grid-cols-2 gap-3">
                  {productTypes.map(product => (
                    <label key={product} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.products.includes(product)}
                        onChange={() => handleMultiSelect('products', product)}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-slate-700 text-sm">{product}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Operating States (Select all that apply)</label>
                <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto border border-slate-200 rounded-lg p-3">
                  {states.map(state => (
                    <label key={state} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.states.includes(state)}
                        onChange={() => handleMultiSelect('states', state)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-slate-700 text-sm">{state}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-800">Insurance Posture</h2>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Current Insurance Market</label>
                <select
                  value={formData.insuranceType}
                  onChange={(e) => handleInputChange('insuranceType', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  <option value="standard">Standard Market</option>
                  <option value="surplus">Surplus Lines</option>
                  <option value="layered">Layered Program (Primary + Excess)</option>
                  <option value="captive">Captive</option>
                  <option value="unsure">Unsure</option>
                </select>
              </div>

              {formData.insuranceType && formData.insuranceType !== 'unsure' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.hasPollutionCoverage}
                      onChange={(e) => handleInputChange('hasPollutionCoverage', e.target.checked)}
                      className="w-6 h-6 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-slate-800 font-medium block">Pollution Coverage in Place</span>
                      <span className="text-slate-600 text-sm">Check if you currently carry pollution liability insurance</span>
                    </div>
                  </label>
                </div>
              )}

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-6">
                <p className="text-sm text-blue-800">
                  You're ready to generate your preliminary risk assessment. This typically takes a few seconds.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-between mt-8 pt-6 border-t">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Back
              </button>
            )}
            
            {step < 5 ? (
              <button
                onClick={() => setStep(step + 1)}
                className="ml-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Continue
              </button>
            ) : (
              <button
                onClick={generateReport}
                className="ml-auto px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
              >
                Generate Report
              </button>
            )}
          </div>
        </div>

        <div className="text-center mt-6 text-sm text-slate-500">
          <p>Secure • Confidential • No information stored</p>
        </div>
      </div>
    </div>
    </>
  );
};

export default RiskAssessmentTool;
